<template>
  <div class="pt-3 border-t border-gray-100 mb-4">
    <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">AI Recommendation</div>
    <div class="flex items-center gap-2 flex-wrap">
      <span :class="['px-2 py-0.5 text-xs font-semibold rounded', dispositionClass]">{{ dispositionLabel }}</span>
      <span v-if="confidenceLabel" class="text-xs text-gray-500">{{ confidenceLabel }} confidence</span>
      <span v-if="runDateLabel" class="text-xs text-gray-400">Run {{ runDateLabel }}</span>
    </div>

    <div v-if="skilledCareNeeds.length" class="mt-2 flex items-center gap-1.5 flex-wrap">
      <span class="text-xs text-gray-500">Skilled care</span>
      <span
        v-for="need in skilledCareNeeds"
        :key="need"
        class="px-1.5 py-px text-xs rounded bg-gray-100 text-gray-700"
      >{{ need }}</span>
    </div>

    <div v-if="pillars.length" class="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
      <template v-for="pillar in pillars" :key="pillar.key">
        <span class="text-xs text-gray-500">{{ pillar.label }}</span>
        <span :class="['text-xs font-medium', pillar.className]">{{ pillar.value }}</span>
      </template>
    </div>

    <div v-if="reason" class="mt-2">
      <div class="text-xs text-gray-500 mb-0.5">Rationale</div>
      <p class="text-xs text-gray-800 whitespace-pre-wrap">{{ reason }}</p>
    </div>

    <div v-if="hasClaimsSummary" class="mt-2.5 pt-2 border-t border-gray-100 space-y-1.5">
      <div class="text-xs font-semibold text-gray-500">Claims Summary</div>
      <div v-if="claimsSummary.certification_period" class="text-xs text-gray-700">
        <span class="text-gray-500">Certification period:</span> {{ claimsSummary.certification_period }}
      </div>
      <div v-if="claimsSummary.episode_number" class="text-xs text-gray-700">
        <span class="text-gray-500">Episode:</span> {{ claimsSummary.episode_number }}
      </div>
      <div v-if="claimsSummary.intermittent_status" class="text-xs text-gray-700">
        <span class="text-gray-500">Intermittent status:</span> {{ claimsSummary.intermittent_status }}
      </div>
      <div v-if="claimsVisitHistory.length">
        <div class="text-xs text-gray-500 mb-0.5">Visit history</div>
        <ul class="space-y-0.5">
          <li v-for="(item, idx) in claimsVisitHistory" :key="'vh-' + idx" class="text-xs text-gray-700 list-disc ml-4">{{ item }}</li>
        </ul>
      </div>
      <div v-if="claimsUtilizationFlags.length">
        <div class="text-xs text-gray-500 mb-0.5">Utilization flags</div>
        <ul class="space-y-0.5">
          <li v-for="(item, idx) in claimsUtilizationFlags" :key="'uf-' + idx" class="text-xs text-gray-700 list-disc ml-4">{{ item }}</li>
        </ul>
      </div>
    </div>

    <div v-if="clinicalSections.length" class="mt-2.5 pt-2 border-t border-gray-100 space-y-1.5">
      <div class="text-xs font-semibold text-gray-500">Clinical Summary</div>
      <div v-if="functionalStatusTrend" class="text-xs text-gray-700">
        <span class="text-gray-500">Functional trend:</span> {{ functionalStatusTrend }}
      </div>
      <div v-for="section in clinicalSections" :key="section.key">
        <div class="text-xs text-gray-500 mb-0.5">{{ section.label }}</div>
        <ul class="space-y-0.5">
          <li v-for="(item, idx) in section.items" :key="section.key + '-' + idx" class="text-xs text-gray-700 list-disc ml-4">{{ item }}</li>
        </ul>
      </div>
    </div>

    <div v-if="sourceOfTruth" class="mt-2.5 pt-2 border-t border-gray-100">
      <div class="text-xs font-semibold text-gray-500 mb-0.5">Criterion Evidence</div>
      <p class="text-xs text-gray-800 whitespace-pre-wrap">{{ sourceOfTruth }}</p>
    </div>

    <div v-if="dataQualityNotes" class="mt-2.5 pt-2 border-t border-gray-100">
      <div class="text-xs font-semibold text-gray-500 mb-0.5">Data Quality Notes</div>
      <p class="text-xs text-gray-700 whitespace-pre-wrap">{{ dataQualityNotes }}</p>
    </div>

    <div v-if="missingData.length" class="mt-2">
      <div class="text-xs text-gray-500 mb-0.5">Data gaps to verify</div>
      <ul class="space-y-1">
        <li v-for="(item, idx) in missingData" :key="idx" class="text-xs text-gray-700 list-disc ml-4">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>

const DISPOSITIONS = {
  APPROVE: { label: 'Continue home health', className: 'bg-green-100 text-green-800' },
  DISCHARGE: { label: 'Recommend discharge', className: 'bg-red-100 text-red-700' },
  NEEDS_REVIEW: { label: 'Needs review', className: 'bg-amber-100 text-amber-800' },
};

const PILLARS = [
  { key: 'homebound_status', label: 'Homebound' },
  { key: 'skilled_need_status', label: 'Skilled need' },
  { key: 'recertification_validity', label: 'Recertification' },
  { key: 'physician_plan_of_care_status', label: 'Plan of care' },
];

const PILLAR_CLASSES = {
  MET: 'text-green-700',
  CURRENT: 'text-green-700',
  NOT_MET: 'text-red-600',
  LAPSED: 'text-red-600',
  INSUFFICIENT_DATA: 'text-amber-700',
};

const SKILLED_CARE_LABELS = {
  nursing: 'Nursing',
  PT: 'PT',
  OT: 'OT',
  SLP: 'SLP',
};

const CLINICAL_SUMMARY_SECTIONS = [
  { key: 'primary_diagnoses', label: 'Primary diagnoses' },
  { key: 'homebound_evidence', label: 'Homebound evidence' },
  { key: 'skilled_service_rationale', label: 'Skilled service rationale' },
  { key: 'goal_progress', label: 'Goal progress' },
  { key: 'recent_clinical_changes', label: 'Recent clinical changes' },
  { key: 'relevant_medications', label: 'Relevant medications' },
];

export default {
  name: 'Patient_Task_AIRecommendation',
  props: {
    metadata: {
      type: Object,
      required: true,
    },
    patientIdentifier: {
      type: [String, Number],
      default: null,
    },
  },
  computed: {
    disposition() {
      return DISPOSITIONS[this.metadata.ai_final_disposition] || null;
    },
    dispositionLabel() {
      return this.disposition?.label || this.metadata.ai_final_disposition || 'No recommendation';
    },
    dispositionClass() {
      return this.disposition?.className || 'bg-gray-100 text-gray-700';
    },
    confidenceLabel() {
      const value = Number(this.metadata.ai_confidence);
      if (!Number.isFinite(value)) return '';
      return `${Math.round(value * 100)}%`;
    },
    runDateLabel() {
      const raw = this.metadata.ai_run_date;
      if (!raw) return '';
      const [year, month, day] = String(raw).slice(0, 10).split('-').map(Number);
      return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },
    skilledCareNeeds() {
      const needs = this.metadata.ai_skilled_care_needs;
      if (!Array.isArray(needs)) return [];
      return needs.map((need) => SKILLED_CARE_LABELS[need] || need);
    },
    pillars() {
      const assessment = this.metadata.ai_eligibility_assessment;
      if (!assessment) return [];
      return PILLARS.filter((pillar) => assessment[pillar.key]).map((pillar) => {
        const raw = assessment[pillar.key];
        return {
          key: pillar.key,
          label: pillar.label,
          value: String(raw).replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase()),
          className: PILLAR_CLASSES[raw] || 'text-gray-700',
        };
      });
    },
    reason() {
      return this.metadata.ai_recommendation_reason || '';
    },
    sourceOfTruth() {
      return this.metadata.ai_recommendation_source_of_truth || '';
    },
    claimsSummary() {
      return this.metadata.ai_claims_summary || {};
    },
    claimsVisitHistory() {
      const items = this.claimsSummary.visit_history;
      return Array.isArray(items) ? items.filter(Boolean) : [];
    },
    claimsUtilizationFlags() {
      const items = this.claimsSummary.utilization_flags;
      return Array.isArray(items) ? items.filter(Boolean) : [];
    },
    hasClaimsSummary() {
      return Boolean(
        this.claimsSummary.certification_period
        || this.claimsSummary.episode_number
        || this.claimsSummary.intermittent_status
        || this.claimsVisitHistory.length
        || this.claimsUtilizationFlags.length,
      );
    },
    clinicalSummary() {
      return this.metadata.ai_clinical_summary || {};
    },
    functionalStatusTrend() {
      const val = this.clinicalSummary.functional_status_trend;
      if (!val || val === '(not provided)') return '';
      return val;
    },
    clinicalSections() {
      return CLINICAL_SUMMARY_SECTIONS.map((s) => ({
        ...s,
        items: Array.isArray(this.clinicalSummary[s.key]) ? this.clinicalSummary[s.key].filter(Boolean) : [],
      })).filter((s) => s.items.length > 0);
    },
    dataCompleteness() {
      return this.metadata.ai_data_completeness || {};
    },
    dataQualityNotes() {
      return this.dataCompleteness.data_quality_notes || '';
    },
    missingData() {
      const items = this.metadata.ai_missing_or_uncertain_data;
      return Array.isArray(items) ? items.filter(Boolean) : [];
    },
  },
};
</script>
